import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Globe, Users, Briefcase, TrendingUp } from "lucide-react";

const DemographicsSection = () => {
  const audienceProfile = [
    { label: "North America", percentage: 60, color: "bg-yellow-500", breakdown: "USA 83% / CAN 17%" },
    { label: "Asia", percentage: 19, color: "bg-yellow-400" },
    { label: "Europe", percentage: 14, color: "bg-yellow-300" },
    { label: "Other", percentage: 7, color: "bg-yellow-200" }
  ];

  const demographics = [
    { 
      icon: Users,
      title: "Age Demographics",
      stat: "58%", 
      description: "Ages 35-64 years",
      detail: "Prime decision-making demographic"
    },
    { 
      icon: Briefcase,
      title: "Decision Makers", 
      stat: "68%",
      description: "Authorized to make purchases",
      detail: "Key industry influencers"
    },
    { 
      icon: TrendingUp,
      title: "High Net Worth",
      stat: "40%", 
      description: "Portfolios over $250K",
      detail: "Significant investment capacity"
    },
    { 
      icon: Globe,
      title: "Income Level",
      stat: "39%",
      description: "Household income $100K+", 
      detail: "Affluent target audience"
    }
  ];

  const audienceInterests = [
    { category: "Finance & Investment", multiplier: "8x", description: "More likely to engage with financial content" },
    { category: "Travel & Leisure", multiplier: "8x", description: "Higher engagement with luxury travel" },
    { category: "Government & Politics", multiplier: "8x", description: "Above-average political engagement" },
    { category: "Technology & Electronics", multiplier: "8x", description: "Early adopters of new technology" },
    { category: "Health & Medicine", multiplier: "5x", description: "Premium healthcare interest" },
    { category: "Automotive", multiplier: "4x", description: "Luxury vehicle market" }
  ];

  return (
    <section className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-yellow-500 mb-4">
            Audience Demographics & Profile
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto">
            Reach the most influential and affluent professionals in the mining industry with precise targeting and verified demographics.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Geographic Distribution */}
          <Card className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/60 hover:shadow-[0_0_25px_rgba(244,185,66,0.25)] hover:bg-gray-800/90 transition-all duration-500 group hover:-translate-y-2 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-yellow-500/5 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-all before:duration-500">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-3">
                <Globe className="w-6 h-6 text-yellow-500" />
                Geographic Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {audienceProfile.map((region, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="text-white font-medium">{region.label}</span>
                      <span className="text-yellow-500 font-bold">{region.percentage}%</span>
                    </div>
                    {region.breakdown && (
                      <div className="text-gray-400 text-sm mb-1">
                        {region.breakdown}
                      </div>
                    )}
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full ${region.color} transition-all duration-1000 ease-out`}
                        style={{ width: `${region.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-gray-500 text-xs mb-4">*Source: Google GA4 (this year to date)</p>
              </div>
              
              <div className="mt-6 text-center">
                <div className="text-2xl font-bold text-yellow-500">65%</div>
                <div className="text-gray-400 text-sm">Male | 35% Female</div>
                <p className="text-gray-500 text-xs mt-2">Source: *SimilarWeb (JAN to AUG 2025)</p>
              </div>
            </CardContent>
          </Card>

          {/* Key Demographics */}
          <div className="grid grid-cols-2 gap-4">
            {demographics.map((demo, index) => {
              const IconComponent = demo.icon;
              return (
                <Card key={index} className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/60 hover:shadow-[0_0_15px_rgba(244,185,66,0.3)] hover:bg-gray-700/50 transition-all duration-300 group hover:-translate-y-1 hover:scale-105 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-t before:from-yellow-500/10 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300">
                  <CardContent className="p-6 text-center">
                    <IconComponent className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-yellow-500 mb-1">{demo.stat}</div>
                    <div className="text-white font-semibold text-sm mb-2">{demo.description}</div>
                    <div className="text-gray-400 text-xs">{demo.detail}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Audience Interests */}
        <Card className="bg-gray-800 border-yellow-500/20">
          <CardHeader>
            <CardTitle className="text-white text-center">
              Audience Interest Over-Index
            </CardTitle>
            <p className="text-gray-400 text-center">
              Our audience shows significantly higher engagement in key categories
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {audienceInterests.map((interest, index) => (
                <div key={index} className="text-center p-4 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors">
                  <div className="text-3xl font-bold text-yellow-500 mb-2">
                    {interest.multiplier}
                  </div>
                  <div className="text-white font-semibold mb-2">
                    {interest.category}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {interest.description}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <h3 className="text-2xl font-bold text-white mb-4">
            Reach Your Target Audience with Precision
          </h3>
          <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
            Access retail and institutional investors who have both the authority and capital to invest in mining opportunities.
          </p>
        </div>
      </div>
    </section>
  );
};

export default DemographicsSection;